import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigateRoute } from 'src/utils/routing';

/** A tab that can be addressed by a URL segment. */
type RoutableTab<T extends string> = { value: T; slug: string };

interface UseEntityTabRouteOptions<T extends string> {
  /** Tabs the user may see, in display order. `tabs[0]` is the fallback. */
  tabs: RoutableTab<T>[];
  /**
   * `true` while the available-tab list is still being resolved (group rules, record).
   * Canonicalization is suspended until this is `false`, otherwise a deep link to a
   * permission-gated tab would be judged invalid and redirected away on first paint.
   */
  loading: boolean;
  /** Last selected tab, persisted across visits. Used only when the URL says nothing. */
  savedTab: T;
  /** Builds the full-page path for a slug. Must be referentially stable. */
  buildPath: (slug: string) => string;
  /** Called with the resolved tab so it becomes the next visit's `savedTab`. */
  onTabResolved: (tab: T) => void;
}

/**
 * Makes the selected full-page tab part of the URL (`/sites/{id}/{slug}`).
 *
 * The URL is the single source of truth while this hook is mounted - there is no
 * store -> URL direction, so no tiebreak between the two and no render loop.
 *
 * Resolution order: the tab named by the URL, else `savedTab`, else the first tab.
 */
export const useEntityTabRoute = <T extends string>({
  tabs,
  loading,
  savedTab,
  buildPath,
  onTabResolved,
}: UseEntityTabRouteOptions<T>) => {
  const { entityTab } = useParams<'entityTab'>();
  const { navigate } = useNavigateRoute();

  const urlTab = useMemo(() => tabs.find((tab) => tab.slug === entityTab), [tabs, entityTab]);

  const resolvedTab = useMemo(
    () => urlTab ?? tabs.find((tab) => tab.value === savedTab) ?? tabs[0],
    [urlTab, tabs, savedTab],
  );

  const activeTab = resolvedTab?.value;

  // The segment is missing, unknown, or names a tab this user may not see - rewrite it.
  // A URL that already resolved is never rewritten, so a later `savedTab` change can
  // never yank the address bar out from under the user.
  useEffect(() => {
    if (loading || urlTab || !resolvedTab) {
      return;
    }
    navigate(buildPath(resolvedTab.slug), { replace: true });
  }, [loading, urlTab, resolvedTab, navigate, buildPath]);

  // Keep "last selected tab" fresh, and keep the store-driven nested strip (side panel,
  // mobile) showing the same tab. One-way: URL -> store.
  useEffect(() => {
    if (!activeTab || activeTab === savedTab) {
      return;
    }
    onTabResolved(activeTab);
  }, [activeTab, savedTab, onTabResolved]);

  const setActiveTab = useCallback(
    (value: T) => {
      const slug = tabs.find((tab) => tab.value === value)?.slug;
      if (slug) {
        // push, so Back walks back through the tabs the user visited
        navigate(buildPath(slug));
      }
    },
    [tabs, navigate, buildPath],
  );

  return { activeTab, setActiveTab };
};
